import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { queryKeys } from '@/lib/queryKeys';
import { invalidateAlertes } from '@/lib/queryInvalidation';
import { queueOrExecuteInvalidation } from '@/lib/modalProtection';
import type { CalendarAffectation, AffectationFormData } from '@/types/calendar';

interface UseCalendarAffectationsProps {
  entrepriseId: string | null;
}

// Données brutes retournées par la query combinée
interface CalendarData {
  affectations: CalendarAffectation[];
  chantiers: any[];
  membres: any[];
  equipes: any[];
}

/**
 * Hook pour charger les affectations du calendrier/planning.
 * Migré vers React Query (useQuery + useMutation + Realtime invalidation).
 */
export function useCalendarAffectations({ entrepriseId }: UseCalendarAffectationsProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const enabled = !!entrepriseId;

  // -------------------------------------------------------------------------
  // Query combinée : affectations + chantiers + membres + equipes
  // -------------------------------------------------------------------------
  const {
    data,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.affectations.calendarAll(entrepriseId ?? ''),
    queryFn: async (): Promise<CalendarData> => {
      if (import.meta.env.DEV) console.log('[useCalendarAffectations] Loading all data...');

      // Charger les affectations avec relations
      const { data: affectationsData, error: affError } = await supabase
        .from('affectations_chantiers')
        .select(`
          id, chantier_id, membre_equipe_id, equipe_id, date_debut, date_fin,
          heures_par_jour, jours_travailles, taux_horaire_specifique,
          charges_salariales_pct, charges_patronales_pct, notes,
          created_at, updated_at, jours_details, heure_debut,
          chantier:chantiers(id, nom_chantier, client, adresse, etat_chantier, pictogramme, couleur, telephone_client),
          membre:membres_equipe(id, nom, prenom, taux_horaire, poste, specialite, charges_salariales, charges_patronales, charges_salariales_pct, charges_patronales_pct, coefficient_charges, heures_par_jour),
          equipe:equipes(id, nom, specialite, pictogramme, couleur)
        `)
        .order('date_debut', { ascending: true });

      if (affError) throw affError;

      // Filtrer les affectations par entreprise (via chantier)
      const { data: chantiersEntreprise } = await supabase
        .from('chantiers')
        .select('id')
        .eq('entreprise_id', entrepriseId!);

      const chantierIds = new Set((chantiersEntreprise || []).map((c) => c.id));
      const filteredAffectations = (affectationsData || []).filter((a) =>
        chantierIds.has(a.chantier_id)
      );

      // Charger les chantiers actifs
      const { data: chantiersData } = await supabase
        .from('chantiers')
        .select('id, nom_chantier, client, adresse, etat_chantier')
        .eq('entreprise_id', entrepriseId!)
        .in('etat_chantier', ['planifie', 'en_cours', 'suspendu', 'termine', 'archive'])
        .order('nom_chantier');

      // Charger les membres
      const { data: membresData } = await supabase
        .from('membres_equipe')
        .select('*')
        .eq('entreprise_id', entrepriseId!)
        .in('statut', ['disponible', 'sur_chantier'])
        .order('nom');

      // Charger les équipes
      const { data: equipesData } = await supabase
        .from('equipes')
        .select('*')
        .eq('entreprise_id', entrepriseId!)
        .order('nom');

      return {
        affectations: filteredAffectations as CalendarAffectation[],
        chantiers: chantiersData || [],
        membres: membresData || [],
        equipes: equipesData || [],
      };
    },
    enabled,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // -------------------------------------------------------------------------
  // Realtime : invalider la query quand les affectations changent
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!entrepriseId) return;

    const channel = supabase
      .channel('calendar-affectations')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'affectations_chantiers',
        },
        () => {
          if (import.meta.env.DEV) console.log('[useCalendarAffectations] Realtime, invalidating...');
          queueOrExecuteInvalidation(() => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.affectations.calendarAll(entrepriseId),
            });
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [entrepriseId, queryClient]);

  // -------------------------------------------------------------------------
  // Mutation : créer une affectation
  // -------------------------------------------------------------------------
  const createMutation = useMutation({
    mutationFn: async (formData: AffectationFormData) => {
      const membre = (data?.membres || []).find((m: any) => m.id === formData.membre_equipe_id);

      const insertData = {
        chantier_id: formData.chantier_id,
        membre_equipe_id: formData.membre_equipe_id,
        equipe_id: formData.equipe_id || null,
        date_debut: formData.date_debut,
        date_fin: formData.date_fin,
        heures_par_jour: formData.heures_par_jour,
        jours_travailles: formData.jours_travailles,
        taux_horaire_specifique: membre?.taux_horaire || null,
        charges_salariales_pct: membre?.charges_salariales_pct || membre?.charges_salariales || null,
        charges_patronales_pct: membre?.charges_patronales_pct || membre?.charges_patronales || null,
        notes: formData.notes || null,
      };

      const { error } = await supabase.from('affectations_chantiers').insert(insertData);
      if (error) throw error;

      return formData;
    },
    onSuccess: (_result, formData) => {
      toast({
        title: 'Affectation créée',
        description: 'Le membre a été affecté au chantier',
      });

      if (entrepriseId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.affectations.calendarAll(entrepriseId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.equipe.stats(entrepriseId) });
        // Invalider les alertes (remplace l'ancien event affectation-created)
        invalidateAlertes(queryClient, entrepriseId);
      }
    },
    onError: (error: any) => {
      console.error('Erreur création affectation:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de créer l\'affectation',
        variant: 'destructive',
      });
    },
  });

  // -------------------------------------------------------------------------
  // Mutation : mettre à jour une affectation
  // -------------------------------------------------------------------------
  const updateMutation = useMutation({
    mutationFn: async ({ id, formData }: { id: string; formData: Partial<AffectationFormData> }) => {
      const updateData: any = {};
      if (formData.chantier_id) updateData.chantier_id = formData.chantier_id;
      if (formData.membre_equipe_id) updateData.membre_equipe_id = formData.membre_equipe_id;
      if (formData.date_debut) updateData.date_debut = formData.date_debut;
      if (formData.date_fin) updateData.date_fin = formData.date_fin;
      if (formData.heures_par_jour !== undefined) updateData.heures_par_jour = formData.heures_par_jour;
      if (formData.jours_travailles !== undefined) updateData.jours_travailles = formData.jours_travailles;
      if (formData.notes !== undefined) updateData.notes = formData.notes || null;

      const { error } = await supabase
        .from('affectations_chantiers')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: 'Affectation modifiée',
        description: 'Les modifications ont été enregistrées',
      });

      if (entrepriseId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.affectations.calendarAll(entrepriseId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.equipe.stats(entrepriseId) });
      }
    },
    onError: (error: any) => {
      console.error('Erreur mise à jour affectation:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de modifier l\'affectation',
        variant: 'destructive',
      });
    },
  });

  // -------------------------------------------------------------------------
  // Mutation : supprimer une affectation
  // -------------------------------------------------------------------------
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('affectations_chantiers').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: 'Affectation supprimée' });

      if (entrepriseId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.affectations.calendarAll(entrepriseId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.equipe.stats(entrepriseId) });
      }
    },
    onError: (error: any) => {
      console.error('Erreur suppression affectation:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de supprimer l\'affectation',
        variant: 'destructive',
      });
    },
  });

  // -------------------------------------------------------------------------
  // Wrappers rétrocompatibles (async → Promise<boolean>)
  // -------------------------------------------------------------------------
  const createAffectation = async (formData: AffectationFormData): Promise<boolean> => {
    try {
      await createMutation.mutateAsync(formData);
      return true;
    } catch {
      return false;
    }
  };

  const updateAffectation = async (id: string, formData: Partial<AffectationFormData>): Promise<boolean> => {
    try {
      await updateMutation.mutateAsync({ id, formData });
      return true;
    } catch {
      return false;
    }
  };

  const deleteAffectation = async (id: string): Promise<boolean> => {
    try {
      await deleteMutation.mutateAsync(id);
      return true;
    } catch {
      return false;
    }
  };

  // -------------------------------------------------------------------------
  // API publique identique
  // -------------------------------------------------------------------------
  return {
    affectations: data?.affectations ?? [],
    chantiers: data?.chantiers ?? [],
    membres: data?.membres ?? [],
    equipes: data?.equipes ?? [],
    loading,
    refresh: async (_silentRefresh?: boolean) => { await refetch(); },
    createAffectation,
    updateAffectation,
    deleteAffectation,
  };
}
